// @ts-nocheck
import React, { useState, useRef, memo, useEffect } from "react";
import { GripVertical, Settings } from "lucide-react";
import { Typography, Row } from "antd";
import { formatRoute } from "react-router-named-routes";
import styles from "./ViewSelector.module.scss";
import qs from "query-string";
import { useLocation, useHistory } from "react-router-dom";
import { UsersApiV2UsersMeViewsPutRequest, UserViewsDTO } from "@generated/v2";
import { usePutData } from "app/hooks/hooks";
import { usersMeViewsPut } from "app/api/v2/usersApi";
import { useTestRecords } from "app/state/testRecords";
import { message } from "antd";
import { useTranslation } from "react-i18next";
interface ViewSelectorProps {
  userViews: any[];
  length: number;
  userViewsRoute: string;
  route: string;
}

const { Text } = Typography;

const ViewSelector = ({
  userViews,
  length,
  userViewsRoute,
  route,
}: ViewSelectorProps) => {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();

  const {
    isError,
    isSuccess,
    data: saveData,
    setBody,
  } = usePutData<UserViewsDTO, UsersApiV2UsersMeViewsPutRequest>(
    usersMeViewsPut
  );

  const [views, setViews] = useState(userViews);
  const { views: lastViews, handleViews } = useTestRecords();

  const currentSearch = qs.parse(location.search);
  const viewId = Number(currentSearch?.viewId);
  const lastUpdated = lastViews?.lastUpdated;

  const [hoveredView, setHoveredView] = useState(null);
  const [draggedView, setDraggedView] = useState(null);
  const [dragOverView, setDragOverView] = useState(null);
  const dragCounter = useRef(0);

  const handleDragStart = (e, view) => {
    setDraggedView(view);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (e, view) => {
    e.preventDefault();
    dragCounter.current++;
    setDragOverView(view);
  };

  const handleDragLeave = e => {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverView(null);
    }
  };

  const handleDrop = (e, targetView) => {
    e.preventDefault();
    dragCounter.current = 0;

    if (draggedView && targetView && draggedView.id !== targetView.id) {
      const newViews = [...views];
      const draggedIndex = newViews.findIndex(v => v.id === draggedView.id);
      const targetIndex = newViews.findIndex(v => v.id === targetView.id);

      // Remove dragged item and insert at target position
      const [removed] = newViews.splice(draggedIndex, 1);
      newViews.splice(targetIndex, 0, removed);

      const requestPayload = {
        views: newViews,
        lastUpdated,
      };

      const formData = {
        updateUserViewsCommand: requestPayload,
      };
      setBody(formData);

      setViews(newViews);
    }

    setDraggedView(null);
    setDragOverView(null);
  };

  const handleDragEnd = () => {
    setDraggedView(null);
    setDragOverView(null);
    dragCounter.current = 0;
  };

  const setActiveView = viewId => {
    setViews(
      views.map(view => ({
        ...view,
        active: view.id === viewId,
      }))
    );
  };

  const viewsContainerStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const getViewItemStyle = view => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    height: "30px",
    whiteSpace: "nowrap",
    marginBottom: "4px",
    marginRight: "12px",
    color: view.active ? "#1d4ed8" : "#4b5563",
    ...(dragOverView?.id === view.id && {
      border: "2px dashed #60a5fa",
    }),
    ...(draggedView?.id === view.id && {
      opacity: 0.5,
    }),
  });

  const getDragHandleStyle = viewId => ({
    display: "flex",
    alignItems: "center",
    marginRight: hoveredView === viewId ? "4px" : "0",
    opacity: hoveredView === viewId ? 1 : 0,
    width: hoveredView === viewId ? "auto" : "0",
    transition: "all 0.2s ease",
    cursor: "grab",
  });

  const getSettingsStyle = viewId => ({
    marginLeft: hoveredView === viewId ? "6px" : "0",
    opacity: hoveredView === viewId ? 1 : 0,
    transition: "all 0.2s ease",
  });

  const iconStyle = {
    color: "#9ca3af",
    cursor: "pointer",
    transition: "color 0.2s ease",
    marginTop: "-12px",
  };

  const iconHoverStyle = {
    color: "#6b7280",
  };

  const handleViewChange = (id: number | undefined) => {
    const currentSearch = qs.parse(location.search);
    const newRoute = formatRoute(route);

    const values = {
      ...currentSearch,
      viewId: isNaN(id) ? undefined : id,
    };

    history.replace({
      pathname: newRoute,
      search: qs.stringify(values, {
        arrayFormat: "comma",
      }),
    });
  };

  const handleViewEdit = (id: number | undefined) => {
    const currentSearch = qs.parse(location.search);
    const newRoute = formatRoute(userViewsRoute);
    // and overwrite or add new values
    const values = {
      ...currentSearch,
      viewId: id,
    };

    history.replace({
      pathname: newRoute,
      search: qs.stringify(values, {
        arrayFormat: "comma",
      }),
    });
  };

  /**
   * Listen for the state of the save data hook, and display status
   * message, and closes on success.
   */
  useEffect(() => {
    if (isSuccess) {
      message.success(
        !viewId
          ? t("testRecords.userViewsSuccess")
          : t("testRecords.userViewsSuccessUpdate")
      );
    } else if (isError) {
      message.error(t("testRecords.userViewsError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  useEffect(() => {
    if (!!saveData) {
      handleViews(saveData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveData]);

  useEffect(() => {
    setViews(userViews);
  }, [userViews, length]);

  return (
    <div style={viewsContainerStyle}>
      {views &&
        length > 0 &&
        views?.map(view => (
          <Row
            key={view.id}
            type="flex"
            justify="space-between"
            align="center"
            style={getViewItemStyle(view)}
            onMouseEnter={() => setHoveredView(view.id)}
            onMouseLeave={() => setHoveredView(null)}
            onClick={() => setActiveView(view.id)}
            onDragOver={handleDragOver}
            onDragEnter={e => handleDragEnter(e, view)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, view)}
            g={8}
          >
            {/* Drag handle - only visible on hover */}
            <div
              style={getDragHandleStyle(view.id)}
              draggable={hoveredView === view.id}
              onDragStart={e => handleDragStart(e, view)}
              onDragEnd={handleDragEnd}
            >
              <GripVertical
                size={16}
                style={{
                  ...iconStyle,
                  cursor: "grab",
                  ":active": { cursor: "grabbing" },
                }}
                onMouseEnter={(e: any) =>
                  (e.target.style.color = iconHoverStyle.color)
                }
                onMouseLeave={(e: any) =>
                  (e.target.style.color = iconStyle.color)
                }
              />
            </div>
            <Text
              onClick={() => {
                handleViewChange(view.id);
              }}
              className={styles.viewName}
              style={{
                borderBottom: viewId === view.id ? "2px solid #253772" : "none",
              }}
            >
              {view?.name}
            </Text>
            {/* Settings gear - only visible on hover */}
            <div style={getSettingsStyle(view.id)}>
              <Settings
                size={16}
                style={iconStyle}
                onMouseEnter={e =>
                  (e.target.style.color = iconHoverStyle.color)
                }
                onMouseLeave={e => (e.target.style.color = iconStyle.color)}
                onClick={() => handleViewEdit(view.id)}
              />
            </div>
          </Row>
        ))}
    </div>
  );
};

export default memo(ViewSelector);
